import React from "react";
import { cn, formatNumber } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export interface StageMetric {
  groupCode: string;
  groupName: string;
  status: "COMPLETED" | "IN_PROGRESS" | "LATE" | "PENDING" | "NA";
  completedQty: number;
  totalQty: number;
  planQty?: number;
  actualQty?: number;
  unit?: "sheet" | "pcs" | string;
  formattedDeadline?: string;
  dueText?: string;
  percent: number;
  taskCount: number;
  detailNames: string[];
  targetTime?: string;
  targetDate?: string;
  isLate?: boolean;
  lateNotice?: string;
}

interface Props {
  stage?: StageMetric;
}

export function StageProgressCell({ stage }: Props) {
  // Những công đoạn nào không có trong quy trình của lệnh đó thì đổi màu xám
  if (!stage || stage.status === "NA") {
    return (
      <div className="px-2 py-1.5 flex items-center justify-center h-full w-full bg-slate-100/70 text-slate-300 border-l border-slate-200 select-none">
        <span className="text-xs font-mono font-medium">—</span>
      </div>
    );
  }

  const isCompleted = stage.status === "COMPLETED" || stage.percent >= 100;
  const isLate = stage.status === "LATE" || stage.isLate;
  const isInProgress = stage.status === "IN_PROGRESS";

  const isSheetUnit = [
    "PREPRESS", "PRINTING", "LAMINATION", "COATING",
    "UV_COATING", "MOUNTING", "DIE_CUTTING", "FOIL_STAMPING", "EMBOSSING"
  ].includes(stage.groupCode);
  const unit = stage.unit || (isSheetUnit ? "sheet" : "pcs");
  const planQty = stage.planQty || stage.totalQty || 0;
  const percent = stage.percent || (isCompleted ? 100 : 0);

  const deadline = stage.formattedDeadline || stage.targetTime || "--/--";
  const dueInfo = stage.dueText || (isCompleted ? "Đã xong" : "theo KH");
  const secondLine = isCompleted ? "Đã xong" : `${deadline} (${dueInfo})`;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              "px-2 py-1 flex flex-col justify-center items-center text-center h-full w-full border-l border-slate-200 transition-colors cursor-pointer select-none",
              isCompleted
                ? "bg-emerald-50/60 hover:bg-emerald-100/70 text-emerald-950"
                : isInProgress
                ? "bg-blue-50/60 hover:bg-blue-100/70 text-blue-950"
                : isLate
                ? "bg-rose-50/50 hover:bg-rose-100/60 text-rose-950"
                : "bg-white hover:bg-slate-50 text-slate-900"
            )}
          >
            {/* Dòng đầu: Số lượng kế hoạch (% HT) Ví dụ 1500sheet (50%) */}
            <div className="font-mono text-[11px] font-bold leading-tight whitespace-nowrap">
              {formatNumber(planQty)}{unit}{" "}
              <span
                className={cn(
                  "text-[10px] font-semibold",
                  isCompleted
                    ? "text-emerald-700 font-bold"
                    : isLate
                    ? "text-rose-700 font-bold"
                    : isInProgress
                    ? "text-blue-700 font-bold"
                    : "text-slate-500"
                )}
              >
                ({percent}%)
              </span>
            </div>

            {/* Dòng 2: Thời hạn (tiến độ) --> 29/09 (còn 4 ngày) */}
            <div
              className={cn(
                "text-[10px] font-medium leading-tight mt-0.5 truncate max-w-[125px] whitespace-nowrap",
                isCompleted
                  ? "text-emerald-600 font-semibold"
                  : isLate
                  ? "text-rose-600 font-semibold"
                  : isInProgress
                  ? "text-blue-600 font-semibold"
                  : "text-slate-500"
              )}
            >
              {secondLine}
            </div>
          </div>
        </TooltipTrigger>
        {stage.detailNames && stage.detailNames.length > 0 && (
          <TooltipContent side="top" className="text-xs space-y-1 p-2 max-w-xs shadow-md">
            <div className="font-bold text-primary">{stage.groupName}</div>
            <div className="text-muted-foreground font-medium">Chi tiết: {formatNumber(stage.completedQty || 0)} / {formatNumber(planQty)} {unit} ({percent}%)</div>
            <ul className="list-disc pl-4 space-y-0.5">
              {stage.detailNames.map((name, idx) => (
                <li key={idx}>{name}</li>
              ))}
            </ul>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}
